import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Folder, FileText, Play, AlertTriangle, CheckCircle, Loader2,
  Server, RefreshCw, ChevronRight, Home, CornerLeftUp, XCircle, Cpu, Clock,
} from 'lucide-react';

const ROOT = '/mnt/data0/shared';
// Remember the last folder ACROSS tab switches (the component unmounts when you
// leave the Submit tab). Module-level so it survives remounts but resets to root
// on a full page reload — i.e. "first enter = root", then it sticks to where you
// were, since you often submit many jobs from the same folder.
let lastSubmitPath = ROOT;

// Human summary of the last time a job ran in a case dir (from /api/submit/preview).
function fmtLastRun(lr) {
  if (!lr || !lr.ran) return { icon: '○', text: 'never run', color: '#64748b' };
  const when = lr.ts
    ? new Date(lr.ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'unknown time';
  if (lr.status === 'completed') return { icon: '✓', text: `last run ${when} · completed`, color: '#22c55e' };
  return { icon: '⚠', text: `last run ${when} · did not complete`, color: '#fbbf24' };
}

// Submit a prepared PowerFLOW case to a chosen cluster PROFILE (a named node-set)
// from anywhere in the dashboard. Browse to the case dir (the folder holding the
// .cdi), pick a profile + ranks-per-node, submit. Profiles overlap on shared
// nodes, so a profile whose nodes are already busy is blocked (can't double-book).
export default function Submit() {
  const [path, setPath] = useState(lastSubmitPath);
  const [entries, setEntries] = useState([]);
  const [parent, setParent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listErr, setListErr] = useState('');
  const [preview, setPreview] = useState(null);       // {runnable, runName, cdiCount}
  const [profiles, setProfiles] = useState([]);       // [{id,nodes,head,size,sruEst,busyNodes,available}]
  const [profileId, setProfileId] = useState(null);
  const [sru, setSru] = useState({ used: 0, pool: 450 });   // live license-pool usage
  const [ranks, setRanks] = useState(192);
  const [timesteps, setTimesteps] = useState(300000);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);         // {success, message/error}

  const loadReq = useRef(0);
  const load = useCallback(async (p) => {
    const reqId = ++loadReq.current;   // latest-request-wins: ignore stale responses
    setLoading(true); setListErr('');
    try {
      const res = await axios.get('/api/docs/list', { params: { path: p } });
      if (reqId !== loadReq.current) return;   // a newer navigation superseded this
      if (res.data?.success) {
        setEntries(res.data.entries || []);
        setParent(res.data.parent || null);
        setPath(res.data.path || p);
        lastSubmitPath = res.data.path || p;   // remember for the next tab visit
      } else {
        setListErr(res.data?.error || 'failed to list directory');
      }
    } catch (e) {
      if (reqId === loadReq.current) setListErr(e?.response?.data?.error || 'failed to list directory');
    } finally {
      if (reqId === loadReq.current) setLoading(false);
    }
  }, []);

  // Poll the profiles + live busy state so conflicts stay current.
  const loadProfiles = useCallback(async () => {
    try {
      const res = await axios.get('/api/submit/profiles');
      if (res.data?.success) {
        setProfiles(res.data.profiles || []);
        setProfileId(prev => prev || (res.data.profiles?.[0]?.id ?? null));
        if (res.data.sruPool) setSru({ used: res.data.sruUsed || 0, pool: res.data.sruPool });
      }
    } catch { /* leave as-is */ }
  }, []);

  const previewReq = useRef(0);
  const loadPreview = useCallback(async (p) => {
    const id = ++previewReq.current;   // latest-request-wins
    try {
      const res = await axios.get('/api/submit/preview', { params: { path: p } });
      if (id === previewReq.current) setPreview(res.data?.success ? res.data : null);
    } catch { if (id === previewReq.current) setPreview(null); }
  }, []);
  useEffect(() => { setPreview(null); loadPreview(path); }, [path, loadPreview]);

  useEffect(() => { load(lastSubmitPath); }, [load]);
  useEffect(() => {
    loadProfiles();
    const id = setInterval(loadProfiles, 5000);
    return () => clearInterval(id);
  }, [loadProfiles]);

  const dirs = entries.filter(e => e.type === 'dir');
  const cdiFiles = entries.filter(e => e.type === 'file' && e.name.endsWith('.cdi'));
  const lgiFiles = entries.filter(e => e.type === 'file' && e.name.endsWith('.lgi'));
  const runnable = !!preview?.runnable;
  const multiCdi = preview && preview.cdiCount > 1;
  const selected = profiles.find(p => p.id === profileId) || null;
  const ranksValid = Number.isInteger(ranks) && ranks >= 1 && ranks <= 384;
  const tsValid = Number.isInteger(timesteps) && timesteps >= 1000 && timesteps <= 100000000;
  // A busy cluster is no longer a hard block — the job queues behind the running
  // one (the runscript serializes). So it's allowed; we just flag that it'll wait.
  const willQueue = selected && !selected.available;
  const canSubmit = runnable && ranksValid && tsValid && selected && !submitting;

  const doSubmit = async () => {
    setSubmitting(true); setResult(null);
    try {
      const res = await axios.post('/api/submit', { path, profile: profileId, ranksPerNode: ranks, numTimesteps: timesteps });
      setResult(res.data);
      loadProfiles(); // refresh busy state
      if (res.data?.success) setTimeout(() => loadPreview(path), 1500); // reflect the just-started run
      // stay in this folder (path unchanged) so the next job can be submitted from here
    } catch (e) {
      setResult(e?.response?.data || { success: false, error: 'submit failed' });
    } finally {
      setSubmitting(false); setConfirming(false);
    }
  };

  const rel = path.startsWith(ROOT) ? path.slice(ROOT.length).replace(/^\//, '') : path;
  const crumbs = rel ? rel.split('/') : [];
  const crumbPath = (i) => ROOT + '/' + crumbs.slice(0, i + 1).join('/');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div>
        <h2 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>Submit a Job</h2>
        <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
          Browse to a prepared case directory (the folder containing its <code style={{ color: '#e2e8f0' }}>.cdi</code>),
          pick a cluster profile and ranks per node, and submit. Overlapping profiles whose nodes are busy are blocked.
        </p>
      </div>

      {result && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 12,
          border: `1px solid ${result.success ? '#22c55e55' : '#ef444455'}`,
          backgroundColor: result.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
        }}>
          {result.success
            ? <CheckCircle style={{ width: 18, height: 18, color: '#22c55e', flexShrink: 0 }} />
            : <XCircle style={{ width: 18, height: 18, color: '#ef4444', flexShrink: 0 }} />}
          <span style={{ color: result.success ? '#86efac' : '#fca5a5', fontSize: 13 }}>
            {result.success ? result.message : result.error}
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 20 }}>
        {/* Left: directory browser */}
        <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', overflow: 'hidden' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            padding: '12px 16px', borderBottom: '1px solid #1e293b', fontSize: 12,
          }}>
            <button onClick={() => load(ROOT)} title="shared root" style={crumbBtn}>
              <Home style={{ width: 13, height: 13 }} /> shared
            </button>
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                <ChevronRight style={{ width: 12, height: 12, color: '#475569' }} />
                <button onClick={() => load(crumbPath(i))} style={crumbBtn}>{c}</button>
              </React.Fragment>
            ))}
            <button onClick={() => load(path)} title="refresh" style={{ ...crumbBtn, marginLeft: 'auto' }}>
              <RefreshCw style={{ width: 13, height: 13 }} />
            </button>
          </div>

          <div style={{ maxHeight: 460, overflowY: 'auto' }}>
            {parent && <Row onClick={() => load(parent)} icon={<CornerLeftUp style={i16('#94a3b8')} />} name=".." muted />}
            {loading && (
              <div style={{ padding: 20, color: '#64748b', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <Loader2 className="animate-spin" style={i16('#64748b')} /> loading…
              </div>
            )}
            {listErr && !loading && <div style={{ padding: 20, color: '#fca5a5', fontSize: 13 }}>{listErr}</div>}
            {!loading && !listErr && dirs.map(d => (
              <Row key={d.path} onClick={() => load(d.path)} icon={<Folder style={i16('#60a5fa')} />} name={d.name} chevron />
            ))}
            {!loading && cdiFiles.map(f => (
              <Row key={f.path} icon={<FileText style={i16('#fbbf24')} />} name={f.name} tag="CDI" />
            ))}
            {!loading && runnable && preview?.lastRun && (() => {
              const lr = fmtLastRun(preview.lastRun);
              return (
                <div title="Last time a job was run in this case folder"
                  style={{ padding: '2px 16px 8px 46px', fontSize: 11, color: lr.color, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 700 }}>{lr.icon}</span> {lr.text}
                </div>
              );
            })()}
            {!loading && lgiFiles.map(f => (
              <Row key={f.path} icon={<FileText style={i16('#34d399')} />} name={f.name} tag="LGI" />
            ))}
            {!loading && !listErr && dirs.length === 0 && cdiFiles.length === 0 && lgiFiles.length === 0 && (
              <div style={{ padding: 20, color: '#64748b', fontSize: 13 }}>empty directory</div>
            )}
          </div>
        </div>

        {/* Right: submit panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Case status */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', padding: 16 }}>
            <p style={labelStyle}>Selected case</p>
            {runnable ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <CheckCircle style={{ ...i16('#22c55e'), marginTop: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{preview.runName}</div>
                  <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>runnable case · 1 .cdi found</div>
                  {preview.lgiCount > 0 ? (
                    <div style={{ color: '#34d399', fontSize: 11, marginTop: 2 }}>✓ mesh present · {preview.lgiCount} .lgi</div>
                  ) : (
                    <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 2 }}>⚠ no .lgi — case not discretized (it may fail at decomposition)</div>
                  )}
                  {preview.lastRun && (() => { const lr = fmtLastRun(preview.lastRun); return (
                    <div style={{ color: lr.color, fontSize: 11, marginTop: 2 }}>{lr.icon} {lr.text}</div>
                  ); })()}
                </div>
              </div>
            ) : multiCdi ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fca5a5', fontSize: 12 }}>
                <AlertTriangle style={i16('#ef4444')} /> {preview.cdiCount} .cdi files here — leave exactly one.
              </div>
            ) : (
              <div style={{ color: '#64748b', fontSize: 12 }}>
                This folder isn’t a runnable case. Open the directory that holds the <code>.cdi</code>.
              </div>
            )}
          </div>

          {/* Profile picker */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
              <p style={labelStyle}>Cluster profile</p>
              <span style={{ fontSize: 10, color: '#64748b' }}>
                license <strong style={{ color: sru.used >= sru.pool ? '#f59e0b' : '#22d3ee' }}>{sru.used}</strong>/{sru.pool} SRU · <strong style={{ color: '#cbd5e1' }}>{Math.max(0, sru.pool - sru.used)}</strong> free
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {profiles.map(p => {
                const sel = profileId === p.id;
                const color = p.available ? '#22d3ee' : '#f59e0b';
                return (
                  <button key={p.id} onClick={() => setProfileId(p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                      padding: '9px 11px', borderRadius: 10, cursor: 'pointer',
                      border: `1px solid ${sel ? color : '#1e293b'}`,
                      backgroundColor: sel ? `${color}18` : '#0b1220',
                      opacity: p.available ? 1 : 0.75,
                    }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0, fontWeight: 800, fontSize: 12,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: `${color}22`, color,
                    }}>{p.id}</span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                      <span style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600 }}>{nodeRange(p.nodes)}</span>
                      <span style={{ color: '#64748b', fontSize: 10 }}>
                        {p.size} node{p.size > 1 ? 's' : ''} · head {p.head}
                        {p.sruEst ? (() => {
                          const overBudget = p.available && p.sruEst > (sru.pool - sru.used);
                          return <> · <span title={overBudget ? `needs ~${p.sruEst} SRU but only ${Math.max(0, sru.pool - sru.used)} free — it will queue behind a running job or need a slot freed` : `estimated ${p.sruEst} SRU`}
                            style={{ color: overBudget ? '#f59e0b' : '#64748b', fontWeight: 600 }}>~{p.sruEst} SRU{overBudget ? ' ⚠' : ''}</span></>;
                        })() : ''}
                      </span>
                    </span>
                    <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
                      {p.available
                        ? <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 700 }}>FREE</span>
                        : <span title={`busy: ${p.busyNodes.join(', ')}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#f59e0b', fontWeight: 700 }}>
                            <AlertTriangle style={i12('#f59e0b')} /> BUSY
                          </span>}
                    </span>
                  </button>
                );
              })}
              {profiles.length === 0 && <div style={{ color: '#64748b', fontSize: 12 }}>loading profiles…</div>}
            </div>
          </div>

          {/* Ranks per node */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', padding: 16 }}>
            <p style={labelStyle}>Ranks per node</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Cpu style={i16('#94a3b8')} />
              <input type="number" min={1} max={384} value={ranks}
                onChange={e => { const v = parseInt(e.target.value, 10); setRanks(Number.isNaN(v) ? '' : v); }}
                style={{
                  width: 90, padding: '7px 10px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: '#0b1220', border: `1px solid ${ranksValid ? '#334155' : '#ef4444'}`, color: '#fff',
                }} />
              <span style={{ color: '#64748b', fontSize: 11 }}>
                {selected ? `× ${selected.size} = ` : ''}
                <strong style={{ color: '#cbd5e1' }}>{selected && ranksValid ? ranks * selected.size : '—'}</strong> total procs
              </span>
            </div>
            <p style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
              192 = 1 rank per physical core (recommended). Higher oversubscribes SMT threads.
            </p>
          </div>

          {/* Timesteps */}
          <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', padding: 16 }}>
            <p style={labelStyle}>Timesteps</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <Clock style={i16('#94a3b8')} />
              <input type="number" min={1000} max={100000000} step={10000} value={timesteps}
                onChange={e => { const v = parseInt(e.target.value, 10); setTimesteps(Number.isNaN(v) ? '' : v); }}
                style={{
                  width: 130, padding: '7px 10px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: '#0b1220', border: `1px solid ${tsValid ? '#334155' : '#ef4444'}`, color: '#fff',
                }} />
              <span style={{ color: '#64748b', fontSize: 11 }}>
                {tsValid ? <strong style={{ color: '#cbd5e1' }}>{timesteps.toLocaleString()}</strong> : <span style={{ color: '#ef4444' }}>1,000–100,000,000</span>} steps
              </span>
              {[300000, 500000, 1000000].map(v => (
                <button key={v} type="button" onClick={() => setTimesteps(v)}
                  style={{
                    padding: '4px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: timesteps === v ? '#1d4ed8' : '#0b1220', color: timesteps === v ? '#fff' : '#93c5fd',
                    border: `1px solid ${timesteps === v ? '#1d4ed8' : '#334155'}`,
                  }}>{v >= 1000000 ? `${v / 1000000}M` : `${v / 1000}k`}</button>
              ))}
            </div>
            <p style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
              Default 300,000. Raise it for larger cases or when running on more nodes (more compute finishes more steps in the same wall-clock).
            </p>
          </div>

          {willQueue && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#60a5fa', fontSize: 12, padding: '0 4px' }}>
              <Clock style={{ ...i16('#60a5fa'), marginTop: 1, flexShrink: 0 }} /> {selected.id} is running a job — this one will <strong>queue</strong> and start when it reaches the top.
            </div>
          )}

          <button disabled={!canSubmit} onClick={() => setConfirming(true)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '12px 16px', borderRadius: 12, fontSize: 14, fontWeight: 700, border: 'none',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              backgroundColor: canSubmit ? '#22c55e' : '#1e293b',
              color: canSubmit ? '#052e16' : '#64748b',
            }}>
            {submitting ? <Loader2 className="animate-spin" style={i16('#64748b')} /> : <Play style={i16(canSubmit ? '#052e16' : '#64748b')} />}
            {submitting ? 'Submitting…' : willQueue ? 'Add to Queue' : 'Submit Job'}
          </button>
        </div>
      </div>

      {confirming && selected && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20,
        }} onClick={() => setConfirming(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 16, padding: 24, maxWidth: 480, width: '100%',
          }}>
            <h3 style={{ color: '#fff', fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Confirm submission</h3>
            <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              Submit <strong style={{ color: '#fff', wordBreak: 'break-all' }}>{preview?.runName}</strong> to{' '}
              <strong style={{ color: '#22d3ee' }}>cluster {selected.id}</strong> ({nodeRange(selected.nodes)}) at{' '}
              <strong style={{ color: '#fff' }}>{ranks} ranks/node</strong> = {ranks * selected.size} procs, head {selected.head},{' '}
              for <strong style={{ color: '#fff' }}>{Number(timesteps).toLocaleString()}</strong> timesteps?
            </p>
            <p style={{ color: '#64748b', fontSize: 12, marginTop: 10 }}>
              This launches a real solver run and clears stale run logs in the case directory.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirming(false)} style={{
                padding: '9px 16px', borderRadius: 10, border: '1px solid #334155',
                backgroundColor: 'transparent', color: '#cbd5e1', fontSize: 13, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={doSubmit} disabled={submitting} style={{
                padding: '9px 16px', borderRadius: 10, border: 'none',
                backgroundColor: '#22c55e', color: '#052e16', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>{submitting ? 'Submitting…' : 'Yes, submit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// "node9-node10" contiguous -> "node9–node13"; else comma list.
function nodeRange(nodes) {
  if (!nodes || !nodes.length) return '';
  const nums = nodes.map(n => parseInt(n.replace(/\D/g, ''), 10));
  const contiguous = nums.every((v, i) => i === 0 || v === nums[i - 1] + 1);
  return contiguous && nodes.length > 1 ? `${nodes[0]}–${nodes[nodes.length - 1]}` : nodes.join(', ');
}

const crumbBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent',
  border: 'none', color: '#93c5fd', fontSize: 12, cursor: 'pointer', padding: '2px 4px', borderRadius: 6,
};
const labelStyle = { color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 10px' };
const i16 = (c) => ({ width: 16, height: 16, color: c });
const i12 = (c) => ({ width: 12, height: 12, color: c });

function Row({ onClick, icon, name, chevron, tag, muted }) {
  return (
    <div onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
        cursor: onClick ? 'pointer' : 'default', borderBottom: '1px solid #131c2e',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.backgroundColor = '#131c2e'; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
      {icon}
      <span style={{ color: muted ? '#64748b' : '#e2e8f0', fontSize: 13, flex: 1, wordBreak: 'break-all' }}>{name}</span>
      {tag && <span style={{
        fontSize: 9, fontWeight: 700, color: '#fbbf24', backgroundColor: 'rgba(251,191,36,0.15)',
        padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5,
      }}>{tag}</span>}
      {chevron && <ChevronRight style={i16('#475569')} />}
    </div>
  );
}
